import { z } from "zod"

export const SignupSlackNotificationInputSchema = z.object({
  email: z.string().trim().email().max(254),
  plan: z.enum(["solo", "team"]),
  company: z.string().trim().min(1).max(240),
  firstName: z.string().trim().max(120).optional(),
  lastName: z.string().trim().max(120).optional(),
  phone: z.string().trim().max(80).optional(),
  title: z.string().trim().max(180).optional(),
  linkedinUrl: z.string().trim().url().max(500).optional(),
  signupFlow: z.enum(["legacy", "email_first"]).optional(),
  companySize: z.string().trim().max(120).optional(),
  companyReps: z.string().trim().max(120).optional(),
  companyCountry: z.string().trim().max(120).optional(),
  seniority: z.string().trim().max(120).optional(),
  departments: z.array(z.string().trim().max(120)).max(8).optional(),
  industry: z.string().trim().max(180).optional(),
  employeeCount: z.number().int().positive().optional(),
  revenue: z.string().trim().max(120).optional(),
  companyWebsiteUrl: z.string().trim().url().max(500).optional(),
  companyLinkedinUrl: z.string().trim().url().max(500).optional(),
  companyIndustries: z.array(z.string().trim().max(120)).max(8).optional(),
  technologies: z.array(z.string().trim().max(120)).max(12).optional(),
  smsOptIn: z.boolean().optional(),
  emailMarketingOptIn: z.boolean().optional(),
  signupExperimentKey: z.string().trim().max(120).optional(),
  signupExperimentVariant: z.string().trim().max(120).optional(),
})

export type SignupSlackNotificationInput = z.infer<typeof SignupSlackNotificationInputSchema>

function formatBoolean(value: boolean) {
  return value ? "Yes" : "No"
}

function formatList(value: string[] | undefined) {
  if (!value?.length) {
    return undefined
  }

  return value.join(", ")
}

function formatOptionalField(label: string, value: number | string | undefined) {
  if (value === undefined || value === "") {
    return ""
  }

  return `*${label}:* ${value}\n`
}

export function buildSignupSlackPayload(signupInfo: SignupSlackNotificationInput) {
  const fullName = [signupInfo.firstName, signupInfo.lastName].filter(Boolean).join(" ")
  const planDisplayName = signupInfo.plan === "team" ? "Team" : "Solo"

  return {
    text: `New signup for ${signupInfo.company}`,
    blocks: [
      {
        type: "section",
        text: {
          type: "mrkdwn",
          text:
            `*New Signup:*\n` +
            `*Email:* ${signupInfo.email}\n` +
            `*Company:* ${signupInfo.company}\n` +
            `*Plan:* ${planDisplayName}\n` +
            formatOptionalField("Name", fullName) +
            formatOptionalField("Title", signupInfo.title) +
            formatOptionalField("Phone", signupInfo.phone) +
            formatOptionalField("LinkedIn", signupInfo.linkedinUrl) +
            formatOptionalField("Signup flow", signupInfo.signupFlow) +
            formatOptionalField("Company size", signupInfo.companySize) +
            formatOptionalField("Company reps", signupInfo.companyReps) +
            formatOptionalField("Company country", signupInfo.companyCountry) +
            formatOptionalField("Seniority", signupInfo.seniority) +
            formatOptionalField("Departments", formatList(signupInfo.departments)) +
            formatOptionalField("Industry", signupInfo.industry) +
            formatOptionalField("Employees", signupInfo.employeeCount) +
            formatOptionalField("Revenue", signupInfo.revenue) +
            formatOptionalField("Company website", signupInfo.companyWebsiteUrl) +
            formatOptionalField("Company LinkedIn", signupInfo.companyLinkedinUrl) +
            formatOptionalField("Company industries", formatList(signupInfo.companyIndustries)) +
            formatOptionalField("Technologies", formatList(signupInfo.technologies)) +
            formatOptionalField("SMS opt-in", signupInfo.smsOptIn === undefined ? undefined : formatBoolean(signupInfo.smsOptIn)) +
            formatOptionalField("Marketing email opt-in", signupInfo.emailMarketingOptIn === undefined ? undefined : formatBoolean(signupInfo.emailMarketingOptIn)) +
            formatOptionalField("Experiment key", signupInfo.signupExperimentKey) +
            formatOptionalField("Experiment variant", signupInfo.signupExperimentVariant) +
            `*Source:* ${process.env.NEXT_PUBLIC_FRONT_URL ?? "unknown"}`,
        },
      },
    ],
  }
}
